import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InputComponent } from '../input/input.component';
import { ScanCropModalComponent } from '../scan-crop-modal/scan-crop-modal.component';
import { AlertService } from '../../services/alert.service';
import { OcrService } from '../../services/ocr.service';
import { VehicleReferenceService } from '../../services/vehicle-reference.service';
import { OTHER_OPTION, buildYearOptions } from './vehicle-form';
import { CropRect, ScanMode, VIN_LENGTH, extractPlate, extractVin, preprocessImage } from './scan-parsers';
import { US_STATES } from './us-states';

type MakesState = 'loading' | 'ready' | 'unavailable';
type ModelsState = 'idle' | 'loading' | 'ready' | 'unavailable';

/**
 * The five vehicle fields (year/make/model selects + VIN/plate inputs),
 * shared by the add-vehicle modal and the vehicle-detail edit form. Expects
 * a FormGroup built with buildVehicleForm(). Make/model options come from
 * the API's vPIC proxy; if that is unreachable the selects degrade to plain
 * text inputs so vehicle entry is never blocked.
 */
@Component({
  selector: 'app-vehicle-form-fields',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent],
  templateUrl: './vehicle-form-fields.component.html',
  styleUrls: ['./vehicle-form-fields.component.scss']
})
export class VehicleFormFieldsComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) form!: FormGroup;

  readonly OTHER = OTHER_OPTION;
  readonly states = US_STATES;
  years: string[] = buildYearOptions();
  makes: string[] = [];
  models: string[] = [];
  makesState: MakesState = 'loading';
  modelsState: ModelsState = 'idle';
  /** Which field is mid-scan, if any — drives the spinner and blocks overlap. */
  scanning: ScanMode | null = null;
  /** Separate from `scanning` so the crop dialog doesn't show a reading line. */
  private cropping = false;

  private subs: Subscription[] = [];
  private formSubs: Subscription[] = [];

  constructor(
    private vehicleReferenceService: VehicleReferenceService,
    private ocrService: OcrService,
    private alertService: AlertService,
    private ngbModal: NgbModal,
  ) {}

  ngOnInit(): void {
    this.loadMakes();
    this.wireForm();
  }

  // The parent may replace the FormGroup instance (e.g. vehicle-detail
  // rebuilds its form on every store emission) — rewire and re-reconcile.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['form'] && !changes['form'].firstChange) {
      this.wireForm();
      if (this.makesState === 'ready') {
        this.reconcileControl('make', 'makeOther', this.makes);
        this.maybeLoadModels(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.formSubs.forEach(sub => sub.unsubscribe());
  }

  private wireForm(): void {
    this.formSubs.forEach(sub => sub.unsubscribe());
    this.formSubs = [
      this.form.get('year')!.valueChanges.subscribe(() => this.onYearOrMakeChanged()),
      this.form.get('make')!.valueChanges.subscribe(() => this.onYearOrMakeChanged())
    ];
  }

  private loadMakes(): void {
    this.makesState = 'loading';
    this.subs.push(
      this.vehicleReferenceService.getMakes().subscribe({
        next: makes => {
          this.makes = makes;
          this.makesState = 'ready';
          // Edit mode: snap any preloaded value onto the option list.
          this.reconcileControl('make', 'makeOther', makes);
          this.maybeLoadModels(true);
        },
        error: () => {
          this.makesState = 'unavailable';
          this.degradeToFreeText();
        }
      })
    );
  }

  /** User changed year or make: any previously chosen model is stale. */
  private onYearOrMakeChanged(): void {
    this.form.patchValue({ model: '', modelOther: '' }, { emitEvent: false });
    // Models can't be looked up for an unlisted make — go straight to free text.
    if (this.form.get('make')!.value === OTHER_OPTION) {
      this.form.get('model')!.setValue(OTHER_OPTION, { emitEvent: false });
      this.models = [];
      this.modelsState = 'idle';
      return;
    }
    this.maybeLoadModels(false);
  }

  private maybeLoadModels(reconcile: boolean): void {
    const year = this.form.get('year')!.value ?? '';
    const make = this.form.get('make')!.value ?? '';
    if (this.makesState !== 'ready' || !/^\d{4}$/.test(year) || !make || make === OTHER_OPTION) {
      this.models = [];
      this.modelsState = 'idle';
      return;
    }

    this.modelsState = 'loading';
    this.subs.push(
      this.vehicleReferenceService.getModels(make, year).subscribe({
        next: models => {
          this.models = models;
          this.modelsState = 'ready';
          if (reconcile) {
            this.reconcileControl('model', 'modelOther', models);
          }
        },
        error: () => {
          // Only the model field falls back to free text; makes stay usable.
          this.modelsState = 'unavailable';
        }
      })
    );
  }

  /**
   * Fit an existing (edit-mode / legacy free-text) value to the option list:
   * case-insensitive match snaps to the canonical entry, no match lands on
   * "Other…" with the original text preserved.
   */
  private reconcileControl(selectName: string, otherName: string, options: string[]): void {
    const current = (this.form.get(selectName)!.value ?? '').trim();
    if (!current || current === OTHER_OPTION) {
      return;
    }
    const match = options.find(option => option.toLowerCase() === current.toLowerCase());
    if (match) {
      if (match !== current) {
        this.form.get(selectName)!.setValue(match, { emitEvent: false });
      }
    } else {
      this.form.get(otherName)!.setValue(current, { emitEvent: false });
      this.form.get(selectName)!.setValue(OTHER_OPTION, { emitEvent: false });
    }
  }

  /** vPIC unreachable: make/model become plain text inputs; fold any "Other…" text back in. */
  private degradeToFreeText(): void {
    if (this.form.get('make')!.value === OTHER_OPTION) {
      this.form.get('make')!.setValue(this.form.get('makeOther')!.value, { emitEvent: false });
    }
    if (this.form.get('model')!.value === OTHER_OPTION) {
      this.form.get('model')!.setValue(this.form.get('modelOther')!.value, { emitEvent: false });
    }
  }

  /**
   * A photo came back from the camera: read the field's value out of it and drop
   * it straight into the input, where the user can correct it like any typed
   * value. A failed read leaves the field alone rather than filling it with junk.
   */
  async onScanFile(event: Event, mode: ScanMode): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so re-picking the same file still fires a change event.
    input.value = '';
    if (!file || this.scanning) {
      return;
    }

    if (this.cropping) {
      return;
    }
    this.cropping = true;
    let crop: CropRect | null | undefined;
    try {
      crop = await this.askForCrop(file, mode);
    } finally {
      this.cropping = false;
    }
    if (crop === undefined) {
      return; // dismissed
    }

    this.scanning = mode;
    try {
      const canvas = await preprocessImage(file, crop ?? undefined);
      const text = await this.ocrService.recognize(canvas);

      if (mode === 'vin') {
        const result = extractVin(text);
        if (!result) {
          this.alertService.showSnackBar('Could not read a VIN from that photo. Try again or type it in.');
          return;
        }
        this.applyScan('vin', result.vin);
        if (result.vin.length !== VIN_LENGTH) {
          this.alertService.showSnackBar('Only part of the VIN was legible — please complete it.');
        } else if (!result.checkDigitValid) {
          this.alertService.showSnackBar('Scanned VIN failed its check digit — please verify it.');
        }
      } else {
        const result = extractPlate(text);
        if (!result) {
          this.alertService.showSnackBar('Could not read a plate number from that photo. Try again or type it in.');
          return;
        }
        this.applyScan('licensePlate', result.plate);
        if (result.state) {
          this.applyScan('plateState', result.state);
        }
      }
    } catch (error) {
      // Log it: the user-facing message is deliberately vague, and without this
      // a failure to load the OCR engine is indistinguishable from a bad photo.
      console.error('Scan failed', error);
      this.alertService.showSnackBar('Something went wrong reading that photo. Try again or type it in.');
    } finally {
      this.scanning = null;
    }
  }

  /**
   * Let the user box the text before it is read. Resolves to a crop rect, to
   * null for "scan the whole photo", or to undefined if they backed out.
   */
  private askForCrop(file: File, mode: ScanMode): Promise<CropRect | null | undefined> {
    const modalRef = this.ngbModal.open(ScanCropModalComponent, {
      size: 'lg', centered: true, backdrop: 'static', keyboard: false,
    });
    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.file = file;
    modalRef.componentInstance.mode = mode;
    return modalRef.result.catch(() => undefined);
  }

  /** Read the control fresh — the parent may have swapped the FormGroup mid-scan. */
  private applyScan(controlName: string, value: string): void {
    const control = this.form.get(controlName);
    control?.setValue(value);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  get isMakeOther(): boolean {
    return this.form.get('make')?.value === OTHER_OPTION;
  }

  get isModelOther(): boolean {
    return this.form.get('model')?.value === OTHER_OPTION;
  }

  /** Model renders as free text when make is unlisted or the model lookup failed. */
  get showModelFreeText(): boolean {
    return this.makesState === 'ready' && (this.isMakeOther || this.modelsState === 'unavailable');
  }
}
